import React, { useEffect, useState } from 'react';
import { api } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { TrendingUp, Users, CreditCard, Clock, Sparkles, Award, Target } from 'lucide-react';

const fmtSek = (n) => `${Math.round(n || 0).toLocaleString('sv-SE')} kr`;

const KPI = ({ label, value, sub, icon: Icon, tone = 'neutral', testid }) => {
  const toneClass = tone === 'green' ? 'text-emerald-600' : tone === 'orange' ? 'text-[#E8613A]' : 'text-foreground';
  return (
    <div className="bg-card border border-border rounded-xl p-5" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground">{label}</p>
          <p className={`text-3xl font-bold tracking-tight mt-1 ${toneClass}`}>{value}</p>
          {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
        </div>
        {Icon && <div className="p-2 bg-[#E8613A]/8 rounded-lg"><Icon size={16} className="text-[#E8613A]" /></div>}
      </div>
    </div>
  );
};

const SalesDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.salesStats()
      .then(res => setStats(res.data))
      .catch(() => toast.error('Kunde inte hämta statistik'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-center py-12 text-muted-foreground text-sm" data-testid="sales-dashboard-loading">Laddar…</div>;
  if (!stats) return null;

  return (
    <div className="space-y-6" data-testid="sales-dashboard">
      <div className="bg-gradient-to-br from-[#1A1A1A] to-[#2A1F1A] text-white rounded-xl p-6 border border-[#E8613A]/30">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-[#E8613A] rounded-lg flex items-center justify-center">
            <Award size={18} />
          </div>
          <div>
            {user?.sales_code ? (
              <>
                <p className="text-xs uppercase tracking-wider text-[#E8613A] font-bold">Säljkod</p>
                <h1 className="text-2xl font-bold mt-0.5">{user.sales_code}</h1>
              </>
            ) : (
              <>
                <p className="text-xs uppercase tracking-wider text-[#E8613A] font-bold">Admin-vy</p>
                <h1 className="text-2xl font-bold mt-0.5">Operio Sales</h1>
              </>
            )}
            <p className="text-sm text-white/70 mt-1">
              Välkommen tillbaka, {user?.name?.split(' ')[0] || 'säljare'}! Här är din säljpipeline.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI label="MRR (Betalande)" value={fmtSek(stats.mrr)} sub={`ARR ${fmtSek(stats.arr)}`} icon={CreditCard} tone="orange" testid="sales-kpi-mrr" />
        <KPI label="MRR Pipeline" value={fmtSek(stats.mrr_pipeline)} sub="Inkl. trials" icon={Sparkles} testid="sales-kpi-pipeline" />
        <KPI label="Mina kunder" value={stats.customers_total} sub={`${stats.paying} betalande`} icon={Users} tone="green" testid="sales-kpi-customers" />
        <KPI label="Trials" value={stats.trial} sub={`${stats.demo} demo, ${stats.paused} pausade`} icon={Clock} testid="sales-kpi-trials" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="text-sm font-bold flex items-center gap-2 mb-3"><Target size={14} className="text-[#E8613A]" /> Per bindningstid</h3>
          <div className="space-y-3">
            {[6, 12, 24].map(m => {
              const c = stats.by_contract[m] || 0;
              const total = Object.values(stats.by_contract).reduce((s, x) => s + x, 0) || 1;
              const pct = Math.round((c / total) * 100);
              return (
                <div key={m}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="font-semibold">{m} mån</span>
                    <span className="text-muted-foreground">{c} företag · {pct}%</span>
                  </div>
                  <div className="h-2 rounded-full bg-muted overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-[#E8613A] to-[#D4532F]" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="text-sm font-bold flex items-center gap-2 mb-3"><TrendingUp size={14} className="text-[#E8613A]" /> Per paket</h3>
          <div className="space-y-2">
            {Object.entries(stats.by_plan).filter(([, c]) => c > 0).map(([k, c]) => (
              <div key={k} className="flex justify-between items-center py-1.5 border-b border-border/40 last:border-0">
                <span className="text-sm font-semibold capitalize">{k}</span>
                <span className="text-sm text-muted-foreground">{c} kund{c !== 1 ? 'er' : ''}</span>
              </div>
            ))}
            {Object.values(stats.by_plan).every(c => !c) && (
              <p className="text-xs text-muted-foreground italic">Inga kunder ännu</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesDashboard;
